import { AnimatePresence } from 'framer-motion'

import { NativePlatform, WebPlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { MessageIndicator } from '@/modules/task/components/message-indicator/MessageIndicator'
import { ProgressBar } from '../../progress/ProgressBar'
import { H2 } from '../../typography/Heading'
import {
  StAppHeaderContainer,
  StAppHeaderContentContainer,
  StAppPrimaryActionContainer,
  StHeaderAction,
  StHeaderSide,
  StInlineActions,
  StInlineProgressBar,
  StInlineTitle,
  StProgressBarContainer,
} from './AppHeader.styled'
import { AppHeaderAction } from './AppHeaderAction'
import { AppHeaderProgressText } from './AppHeaderProgressText'
import { AppHeaderTitle } from './AppHeaderTitle'
import { ProfileButton } from './ProfileButton'

import type { FC } from 'react'
import type { AppHeaderProps } from './AppHeader.types'

const AppHeader: FC<AppHeaderProps> = ({
  title,
  titleAfter,
  primaryAction,
  secondaryAction,
  profileButtonEnabled,
  progressText,
  progress,
  progressLayout = 'below',
  backgroundColor,
  enableNotifications,
  centered,
  className,
}: AppHeaderProps) => {
  const isCentered = centered !== false
  const isInlineMode = progressLayout === 'inline' && !!progress

  const leftContent = (
    <AnimatePresence>
      {primaryAction ? (
        <StAppPrimaryActionContainer titleIsVisible={!!title}>
          <AppHeaderAction key='app-header-primary-action' action={primaryAction} style={primaryAction?.style} />
        </StAppPrimaryActionContainer>
      ) : null}
    </AnimatePresence>
  )

  const rightContent = (
    <AnimatePresence>
      {
        <AppHeaderAction
          key='app-header-progress-action'
          action={secondaryAction}
          reverse={secondaryAction?.reverse ?? true}
          style={secondaryAction?.style}
        />
      }

      {enableNotifications ? (
        <NativePlatform>
          <StHeaderAction>
            <MessageIndicator />

            {profileButtonEnabled && <ProfileButton />}
          </StHeaderAction>
        </NativePlatform>
      ) : null}
    </AnimatePresence>
  )

  return (
    <>
      <StAppHeaderContainer backgroundColor={backgroundColor} className={className}>
        <StAppHeaderContentContainer>
          {isCentered && !isInlineMode ? <StHeaderSide>{leftContent}</StHeaderSide> : leftContent}

          {progressLayout === 'inline' && progress ? (
            <>
              {/* Desktop: title as flex item (truncates if too long, never overlaps bar) */}
              {progressText ? (
                <WebPlatform>
                  <StInlineTitle>
                    <H2 textAlign='center' noMargin>
                      {progressText}
                    </H2>
                  </StInlineTitle>
                </WebPlatform>
              ) : null}
              {/* Mobile: bar fills the center slot inline */}
              <NativePlatform>
                <StInlineProgressBar>
                  <ProgressBar {...progress} />
                </StInlineProgressBar>
              </NativePlatform>
            </>
          ) : (
            <>
              <AnimatePresence>
                {title ? (
                  <AppHeaderTitle
                    key='app-header-title'
                    title={typeof title === 'string' ? { title, as: 'h2' } : title}
                    titleAfter={titleAfter}
                    centered={centered}
                  />
                ) : null}
              </AnimatePresence>

              {progress ? <AppHeaderProgressText progress={progress} text={progressText} /> : null}
            </>
          )}

          {isCentered && !isInlineMode ? (
            <StHeaderSide $align='right'>{rightContent}</StHeaderSide>
          ) : isInlineMode ? (
            <StInlineActions>{rightContent}</StInlineActions>
          ) : (
            rightContent
          )}
        </StAppHeaderContentContainer>

        {progressLayout !== 'inline' && progress ? (
          <StProgressBarContainer>
            <ProgressBar {...progress} />
          </StProgressBarContainer>
        ) : null}

        {progressLayout === 'inline' && progress ? (
          <WebPlatform>
            <StProgressBarContainer>
              <ProgressBar {...progress} />
            </StProgressBarContainer>
          </WebPlatform>
        ) : null}
      </StAppHeaderContainer>
    </>
  )
}

export { AppHeader }
